
import { createContext, useContext, useState, ReactNode } from "react";
import { SortField, SortOrder, ViewMode } from "@/components/candidates/CandidatesViewOptions";

interface CandidatesFilterContextType {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  skillsFilter: string[];
  setSkillsFilter: (skills: string[]) => void;
  statusFilter: string[];
  setStatusFilter: (statuses: string[]) => void;
  functionFilter: string;
  setFunctionFilter: (functionName: string) => void;
  sortField: SortField;
  setSortField: (field: SortField) => void;
  sortOrder: SortOrder;
  setSortOrder: (order: SortOrder) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  clearAllFilters: () => void;
}

interface CandidatesFilterProviderProps {
  children: ReactNode;
  viewMode?: ViewMode;
  setViewMode?: (mode: ViewMode) => void;
}

const CandidatesFilterContext = createContext<CandidatesFilterContextType | undefined>(undefined);

export function CandidatesFilterProvider({ 
  children,
  viewMode: externalViewMode,
  setViewMode: externalSetViewMode
}: CandidatesFilterProviderProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [skillsFilter, setSkillsFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [functionFilter, setFunctionFilter] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [internalViewMode, setInternalViewMode] = useState<ViewMode>("card");
  const [currentPage, setCurrentPage] = useState(1);

  // Use either external view mode state or internal
  const viewMode = externalViewMode !== undefined ? externalViewMode : internalViewMode;
  const setViewMode = externalSetViewMode || setInternalViewMode;

  const clearAllFilters = () => {
    setSearchTerm("");
    setSkillsFilter([]);
    setStatusFilter([]);
    setFunctionFilter("");
    setCurrentPage(1);
  };

  return (
    <CandidatesFilterContext.Provider
      value={{
        searchTerm,
        setSearchTerm,
        skillsFilter,
        setSkillsFilter,
        statusFilter,
        setStatusFilter,
        functionFilter,
        setFunctionFilter,
        sortField,
        setSortField,
        sortOrder,
        setSortOrder,
        viewMode,
        setViewMode,
        currentPage,
        setCurrentPage,
        clearAllFilters,
      }}
    >
      {children}
    </CandidatesFilterContext.Provider>
  );
}

export const useCandidatesFilter = () => {
  const context = useContext(CandidatesFilterContext);
  if (context === undefined) {
    throw new Error("useCandidatesFilter must be used within a CandidatesFilterProvider");
  }
  return context;
};
